import { normalizeIsoCountry } from '@/lib/geo/country';

import type { Permission, Role } from '@/types/auth';
import type { Settings } from '@/types/settings';

export interface UserDataCookie {
  country: string | undefined;
  currency: string | undefined;
  email: string | undefined;
  emailVerified: boolean | undefined;
  id: string | undefined;
  isAuthenticated: boolean | undefined;
  permissions?: Permission[];
  /** Active subscription's Stripe plan ID; SSR derives the pricing-test cohort from it. */
  planId?: number;
  role?: Role[];
  subscriptionType?: string;
}

/**
 * Build the `userData` cookie payload from a settings response.
 *
 * `country` is the ISO 3166-1 alpha-2 code from `settings.country.iso_code_2`,
 * NOT the free-form display name on `settings.user.country` ("United States").
 * Middleware reads this cookie and forwards `country` to GrowthBook for
 * flag-evaluation targeting — see DEV-5797/DEV-5798 for why the display-name
 * form silently mis-routes targeting rules.
 *
 * `role`/`permissions` are forwarded here too because `withGrowthBook` (SSR)
 * reads them back off this cookie for role/permission targeting. They were
 * previously omitted, so server-side `role` was always `[]` and role-based
 * saved groups (e.g. `Pro users` → adaptive-music) never matched in SSR — the
 * page 404'd for users the client had already granted access.
 */
export const buildUserDataCookie = (
  settings: Settings,
  extras: {
    permissions?: Permission[];
    role?: Role[];
    subscriptionType?: string;
  } = {}
): UserDataCookie => {
  const cookie: UserDataCookie = {
    isAuthenticated: settings.user?.is_authenticated,
    id: settings.user?.id?.toString(),
    email: settings.user?.email,
    emailVerified: settings.user?.isEmailVerified,
    country: normalizeIsoCountry(settings.country?.iso_code_2),
    currency: settings.currency?.code,
  };

  if (settings.subscriptionData?.isActive) {
    cookie.planId = settings.subscriptionData.plan;
  }

  if (extras.subscriptionType !== undefined) {
    cookie.subscriptionType = extras.subscriptionType;
  }

  if (extras.role !== undefined) {
    cookie.role = extras.role;
  }

  if (extras.permissions !== undefined) {
    cookie.permissions = extras.permissions;
  }

  return cookie;
};
